/**
 * TicketList — renders a list of ticket cards with status badges, last message preview,
 * and unread indicators.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { FlatList, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

type IconName = React.ComponentProps<typeof MaterialCommunityIcons>["name"];

import { ticketVisitLine } from "@/lib/chat-visit-context";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";
import type { Ticket } from "@/lib/ticket-api";

const channelIcons: Record<string, IconName> = {
    in_app_chat: "chat-outline",
    sms: "message-text-outline",
    email: "email-outline",
    phone_call: "phone-outline"
};

const statusColors: Record<string, string> = {
    open: colors.info,
    in_progress: colors.teal500,
    resolved: colors.success,
    closed: colors.textTertiary
};

function formatTimeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) {
        return "just now";
    }
    if (mins < 60) {
        return `${mins}m ago`;
    }
    const hours = Math.floor(mins / 60);
    if (hours < 24) {
        return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

interface Props {
    tickets: Ticket[];
    onTicketPress: (ticketId: number) => void;
    useFlatList?: boolean;
}

/** One conversation card: channel icon, title, time, preview and unread count. */
export function TicketCard({
    ticket: item,
    onPress
}: {
    ticket: Ticket;
    onPress: (ticketId: number) => void;
}) {
    const hasUnread = item.unreadCount > 0;
    const timeStr = item.lastMessageAt ?? item.updatedAt;

    return (
        <TouchableOpacity style={styles.card} activeOpacity={0.7} onPress={() => onPress(item.id)}>
            <View
                style={[
                    styles.iconContainer,
                    {
                        backgroundColor: (statusColors[item.status] ?? colors.info) + "15"
                    }
                ]}
            >
                <MaterialCommunityIcons
                    name={channelIcons[item.channel] ?? "chat-outline"}
                    size={22}
                    color={statusColors[item.status] ?? colors.info}
                />
            </View>
            <View style={styles.cardContent}>
                <View style={styles.cardHeader}>
                    <Text
                        style={[styles.cardTitle, hasUnread && styles.cardTitleUnread]}
                        numberOfLines={1}
                    >
                        {item.subject ?? `${item.channel.replace(/_/g, " ")} conversation`}
                    </Text>
                    <Text style={[styles.cardTime, hasUnread && styles.cardTimeUnread]}>
                        {formatTimeAgo(timeStr)}
                    </Text>
                </View>
                {/* Which visit the thread is about (Noel's list item 29): bot
                    threads are all titled "Manager update". */}
                {item.visit ? (
                    <View style={styles.visitRow}>
                        <MaterialCommunityIcons
                            name="calendar-clock"
                            size={13}
                            color={colors.textSecondary}
                        />
                        <Text style={styles.visitText} numberOfLines={1}>
                            {ticketVisitLine(item.visit)}
                        </Text>
                    </View>
                ) : null}
                {item.lastMessageContent ? (
                    <Text
                        style={[styles.lastMessage, hasUnread && styles.lastMessageUnread]}
                        numberOfLines={1}
                    >
                        {item.lastMessageSenderType === "rn" ? "You: " : ""}
                        {item.lastMessageContent}
                    </Text>
                ) : (
                    <View style={styles.cardFooter}>
                        <View
                            style={[
                                styles.statusBadge,
                                {
                                    backgroundColor:
                                        (statusColors[item.status] ?? colors.info) + "20"
                                }
                            ]}
                        >
                            <Text
                                style={[
                                    styles.statusText,
                                    { color: statusColors[item.status] ?? colors.info }
                                ]}
                            >
                                {item.status.replace(/_/g, " ")}
                            </Text>
                        </View>
                    </View>
                )}
            </View>
            {hasUnread && (
                <View style={styles.unreadDot}>
                    <Text style={styles.unreadCount}>
                        {item.unreadCount > 9 ? "9+" : String(item.unreadCount)}
                    </Text>
                </View>
            )}
        </TouchableOpacity>
    );
}

export function TicketList({ tickets, onTicketPress, useFlatList = true }: Props) {
    const renderTicket = ({ item }: { item: Ticket }) => (
        <TicketCard ticket={item} onPress={onTicketPress} />
    );

    if (!useFlatList) {
        return (
            <View style={styles.list}>
                {tickets.map((ticket) => (
                    <View key={ticket.id}>{renderTicket({ item: ticket })}</View>
                ))}
            </View>
        );
    }

    return (
        <FlatList
            data={tickets}
            renderItem={renderTicket}
            keyExtractor={(item) => String(item.id)}
            contentContainerStyle={styles.list}
        />
    );
}

const styles = StyleSheet.create({
    list: { paddingHorizontal: spacing.base, paddingBottom: spacing.xl },
    card: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        padding: spacing.base,
        marginBottom: spacing.sm,
        ...shadows.xs
    },
    iconContainer: {
        width: 44,
        height: 44,
        borderRadius: 22,
        alignItems: "center",
        justifyContent: "center",
        marginRight: spacing.md
    },
    cardContent: { flex: 1 },
    cardHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
    },
    cardTitle: {
        flex: 1,
        fontFamily: fonts.regular,
        fontSize: 15,
        color: colors.textPrimary,
        marginRight: spacing.sm
    },
    cardTitleUnread: { fontFamily: fonts.semiBold },
    cardTime: { fontFamily: fonts.regular, fontSize: 12, color: colors.textTertiary },
    cardTimeUnread: { color: colors.teal500, fontFamily: fonts.medium },
    visitRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        marginTop: 2
    },
    visitText: {
        flex: 1,
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary
    },
    lastMessage: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 2
    },
    lastMessageUnread: { color: colors.textPrimary, fontFamily: fonts.medium },
    cardFooter: { flexDirection: "row", marginTop: spacing.xs },
    statusBadge: {
        paddingHorizontal: spacing.sm,
        paddingVertical: 2,
        borderRadius: radius.full
    },
    statusText: {
        fontFamily: fonts.medium,
        fontSize: 11,
        textTransform: "capitalize"
    },
    unreadDot: {
        minWidth: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: colors.teal500,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 5,
        marginLeft: spacing.sm
    },
    unreadCount: {
        fontFamily: fonts.bold,
        fontSize: 11,
        color: colors.surface
    }
});
